import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { fetchPrzegladWysp, FRAZA_RESETU, resetInstancji } from '@/lib/wyspy-sprzatanie';

/**
 * WYCZYSZCZENIE INSTANCJI DO ZERA — w USTAWIENIACH INSTANCJI, nie w Agentach.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ PRZENIESIONE NA ŻĄDANIE (01.09.2026): „»Wyczyść instancję do zera« — przenieś z agenta do
 *  ustawień instancji". Stało przy karcie sprzątania wysp, bo tam powstało — a dotyczy CAŁEJ
 *  instancji, nie agentów. Ekran Agentów odwiedza się codziennie; to jest czynność raz w życiu.
 *
 *  ⚠️ SAMO USUWANIE POJEDYNCZEJ WYSPY ZOSTAJE W AGENTACH, i to nie jest niekonsekwencja: tam stoi
 *  lista wysp z licznikami, po których poznaje się, którą usunąć. Tutaj nie ma czego wybierać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function ResetInstancji() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [otwarty, setOtwarty] = useState(false);
  const [fraza, setFraza] = useState('');
  /** Odinstalowanie agentów ze wszystkich maszyn instancji — domyślnie tak (13.10.2026, decyzja usera). */
  const [odinstalujAgentow, setOdinstalujAgentow] = useState(true);

  /** ⚠️ Trasa ma `SdiOnlyGuard` — ekran chowa to z tego samego powodu, a nie „dla porządku". */
  const isSdi = user?.clientId === null;

  const przeglad = useQuery({ queryKey: ['wyspy-przeglad'], queryFn: fetchPrzegladWysp, enabled: isSdi });

  const reset = useMutation({
    mutationFn: (f: string) => resetInstancji(f, odinstalujAgentow),
    onSuccess: (r) => {
      setOtwarty(false);
      setFraza('');
      toast.success(
        t('agents.reset.done')
          .replace('{wysp}', String(r.wysp))
          .replace('{wezlow}', String(r.wezlow))
          .replace('{wierszy}', r.wierszy.toLocaleString('pl'))
          .replace('{kodow}', String(r.kodowRejestracyjnych)),
      );
      if (r.nieusunieteObiekty > 0) {
        toast.error(t('agents.reset.leftovers').replace('{n}', String(r.nieusunieteObiekty)));
      }
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isSdi) return null;

  const wyspy = przeglad.data?.wyspy ?? [];
  const razem = wyspy.reduce(
    (a, w) => ({
      maszyny: a.maszyny + w.liczniki.maszyny,
      wydania: a.wydania + w.liczniki.wydania,
      wezly: a.wezly + w.liczniki.wezlyPotwierdzone + w.liczniki.wezlyInne,
    }),
    { maszyny: 0, wydania: 0, wezly: 0 },
  );

  return (
    <section className="space-y-2 rounded-lg border border-destructive/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-destructive">{t('agents.reset.title')}</span>
        <Button
          size="sm"
          variant="destructive"
          disabled={wyspy.length === 0}
          onClick={() => {
            setFraza('');
            setOtwarty(true);
          }}
        >
          <Trash2 className="size-4" />
          {t('agents.reset.button')}
        </Button>
      </div>
      <p className="max-w-3xl text-[11px] leading-snug text-muted-foreground">{t('agents.reset.what')}</p>

      <Dialog
        open={otwarty}
        onOpenChange={(o) => {
          if (!o) {
            setOtwarty(false);
            setFraza('');
          }
        }}
      >
        <DialogContent title={t('agents.reset.dialogTitle')}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('agents.reset.summary')
                .replace('{wysp}', String(wyspy.length))
                .replace('{wezlow}', String(razem.wezly))
                .replace('{maszyn}', String(razem.maszyny))
                .replace('{wydan}', razem.wydania.toLocaleString('pl'))}
            </p>
            <p className="text-xs text-muted-foreground">{t('agents.reset.what')}</p>
            <p className="rounded border border-destructive/40 p-2 text-xs text-destructive">
              {t('agents.island.forceAgents')}
            </p>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={odinstalujAgentow} onChange={(e) => setOdinstalujAgentow(e.target.checked)} />
              <span>{t('agents.odinstaluj.zWyspa')}</span>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span>{t('agents.reset.confirm').replace('{fraza}', FRAZA_RESETU)}</span>
              <Input
                className="h-8 font-mono"
                autoComplete="off"
                value={fraza}
                onChange={(e) => setFraza(e.target.value)}
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOtwarty(false);
                setFraza('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              /** ⚠️ Ta sama pobłażliwość co w API: fraza jest rytuałem, nie daną. */
              disabled={reset.isPending || fraza.trim().replace(/\s+/g, ' ').toUpperCase() !== FRAZA_RESETU}
              onClick={() => reset.mutate(fraza.trim())}
            >
              {reset.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {t('agents.reset.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
